import { useEffect, useState, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, inr } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import CreateListingDialog, { CATEGORIES } from "@/components/CreateListingDialog";
import BuyDrawer from "@/components/BuyDrawer";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  Search, Plus, Heart, MapPin, ShieldCheck, MessageCircle, Loader2, Flame, ShoppingBag, Tag,
} from "lucide-react";

const TABS = [
  { id: "all", label: "All Items", icon: ShoppingBag },
  { id: "most-bought", label: "Most Bought", icon: Flame },
  { id: "favorites", label: "Favorites", icon: Heart },
];

function ProductDetail({ listing, onClose, onBuy, onChat, onFavorite, user, onManage }) {
  if (!listing) return null;
  const sold = listing.status === "sold";
  const mine = user && listing.seller?.id === user.id;
  return (
    <Dialog open={!!listing} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl p-0 overflow-hidden gap-0 max-h-[92vh] overflow-y-auto" data-testid="product-detail-dialog">
        <div className="grid md:grid-cols-2">
          <div className="relative bg-slate-100 md:h-full">
            <img src={listing.images?.[0]} alt={listing.title} className="w-full h-64 md:h-full object-cover" />
            <span className="absolute top-3 left-3 text-xs font-medium px-2.5 py-1 rounded-md bg-white/90 backdrop-blur border border-slate-200 text-slate-700">
              {listing.condition}
            </span>
          </div>
          <div className="p-6 flex flex-col">
            <div className="label-mono text-slate-400 flex items-center gap-1.5"><Tag size={12} /> {listing.category}</div>
            <h2 className="mt-2 font-display font-bold text-2xl text-ink leading-tight">{listing.title}</h2>
            <div className="mt-2 font-display font-extrabold text-3xl text-ink">{inr(listing.price)}</div>

            <p className="mt-4 text-sm text-slate-600 leading-relaxed">{listing.description}</p>

            <div className="mt-5 rounded-xl border border-slate-200 p-3.5 flex items-center gap-3">
              <Avatar className="h-11 w-11 border border-slate-200">
                <AvatarImage src={listing.seller?.avatar} />
                <AvatarFallback>{listing.seller?.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-ink text-sm truncate">{listing.seller?.name}</span>
                  <ShieldCheck size={14} className="text-emerald-500 shrink-0" />
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <StarRating value={listing.seller?.rating_avg} size={12} />
                  <span className="text-xs text-slate-500">{listing.seller?.rating_avg?.toFixed(1)} · {listing.seller?.rating_count} ratings</span>
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-start gap-2 text-sm text-slate-600 rounded-xl bg-slate-50 p-3">
              <MapPin size={15} className="text-crimson mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-ink">{listing.pickup_point?.name}</div>
                <div className="text-xs text-slate-500">{listing.pickup_point?.zone} · {listing.pickup_point?.landmark}</div>
              </div>
            </div>

            <div className="mt-auto pt-5 flex gap-3">
              {mine ? (
                <>
                  <Button variant="outline" className="flex-1 h-11 border-slate-300" onClick={() => onFavorite(listing)} data-testid="detail-favorite-btn">
                    <Heart size={16} className={listing.is_favorite ? "fill-crimson text-crimson mr-1.5" : "mr-1.5"} />
                    {listing.is_favorite ? "Saved" : "Save"}
                  </Button>
                  <Button className="flex-[1.6] h-11 bg-ink hover:bg-slate-800 text-white" onClick={() => onManage()} data-testid="detail-manage-btn">
                    {sold ? "Sold — view in My Deals" : "Manage in My Deals"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline" className="flex-1 h-11 border-slate-300"
                    onClick={() => onFavorite(listing)} data-testid="detail-favorite-btn"
                  >
                    <Heart size={16} className={listing.is_favorite ? "fill-crimson text-crimson mr-1.5" : "mr-1.5"} />
                    {listing.is_favorite ? "Saved" : "Save"}
                  </Button>
                  <Button className="flex-1 h-11 bg-slate-100 hover:bg-ink hover:text-white text-ink" onClick={() => onChat(listing)} data-testid="detail-chat-btn">
                    <MessageCircle size={16} className="mr-1.5" /> Chat
                  </Button>
                  <Button
                    className="flex-[1.4] h-11 bg-brand hover:bg-brand-700 text-white"
                    disabled={sold} onClick={() => onBuy(listing)} data-testid="detail-buy-btn"
                  >
                    {sold ? "Sold out" : `Buy · ${inr(listing.price)}`}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Marketplace() {
  const { user, membershipConfig } = useAuth();
  const { openAuth } = useOutletContext();
  const navigate = useNavigate();

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [buyDrawer, setBuyDrawer] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { filter: tab, category, q };
      const { data } = await api.get("/listings", { params });
      setListings(data);
    } catch (e) {
      if (tab === "favorites" && !user) { /* handled by gate */ }
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, [tab, category, q, user]);

  useEffect(() => { load(); }, [load]);

  const requireAuth = () => { if (!user) { openAuth(); return false; } return true; };
  const requireMember = () => {
    if (!requireAuth()) return false;
    if (membershipConfig?.preview_active) return true;
    if (!user.is_member && user.role !== "founder") {
      toast.info("A ₹29/month Campus Pass is required to buy, sell or chat. Your first month is free.");
      navigate("/membership");
      return false;
    }
    return true;
  };

  const onFavorite = async (listing) => {
    if (!requireAuth()) return;
    try {
      const { data } = await api.post(`/listings/${listing.id}/favorite`);
      setListings((prev) => prev.map((l) => (l.id === listing.id ? { ...l, is_favorite: data.is_favorite, favorites_count: data.favorites_count } : l)));
      setActive((a) => (a && a.id === listing.id ? { ...a, is_favorite: data.is_favorite } : a));
      toast.success(data.is_favorite ? "Added to favorites" : "Removed from favorites");
      if (tab === "favorites") load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const onChat = async (listing) => {
    if (!requireMember()) return;
    try {
      const { data } = await api.post("/conversations", { listing_id: listing.id });
      navigate(`/chat?c=${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const onBuy = (listing) => {
    if (!requireMember()) return;
    setActive(null);
    setBuyDrawer(listing);
  };

  const onManage = () => { setActive(null); navigate("/deals"); };

  const onSell = () => { if (requireMember()) setCreateOpen(true); };

  return (
    <div className="mx-auto max-w-7xl px-4 lg:px-8 py-8 lg:py-10">
      {/* Heading */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-ink text-3xl lg:text-4xl tracking-tight">Marketplace</h1>
          <p className="text-slate-500 mt-1.5">Second-hand gear from verified SRM KTR students.</p>
        </div>
        <Button onClick={onSell} className="bg-ink hover:bg-slate-800 text-white h-11 rounded-full px-5" data-testid="sell-item-btn">
          <Plus size={18} className="mr-1.5" /> Sell an item
        </Button>
      </div>

      {/* Search + Tabs */}
      <div className="mt-6 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" className="pl-10 h-11 bg-white" data-testid="marketplace-search" />
        </div>
        <div className="flex gap-2 bg-white border border-slate-200 rounded-full p-1 w-fit">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => { if (t.id === "favorites" && !user) return openAuth(); setTab(t.id); }}
              data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                tab === t.id ? "bg-ink text-white" : "text-slate-600 hover:text-ink"
              }`}
            >
              <t.icon size={15} className={t.id === "favorites" && tab === t.id ? "fill-white" : ""} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Category chips */}
      <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {["all", ...CATEGORIES].map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`category-${c}`}
            className={`whitespace-nowrap text-sm px-3.5 py-1.5 rounded-full border transition-colors ${
              category === c ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
          >
            {c === "all" ? "All categories" : c}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="mt-8">
        {loading ? (
          <div className="grid place-items-center py-24 text-slate-400"><Loader2 className="animate-spin" size={28} /></div>
        ) : listings.length === 0 ? (
          tab === "favorites" ? (
            <div className="text-center py-24" data-testid="favorites-empty">
              <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 grid place-items-center mb-4"><Heart className="text-slate-400" /></div>
              <p className="text-slate-500">No favorites yet — tap the heart on any item.</p>
            </div>
          ) : (q || category !== "all") ? (
            <div className="text-center py-24" data-testid="search-empty">
              <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 grid place-items-center mb-4"><Search className="text-slate-400" /></div>
              <p className="text-slate-500">No items match your search. Try another category or keyword.</p>
            </div>
          ) : (
            <div className="text-center py-20 max-w-md mx-auto" data-testid="marketplace-empty-invite">
              <div className="mx-auto h-16 w-16 rounded-2xl bg-brand/10 grid place-items-center mb-5"><ShoppingBag className="text-brand" size={28} /></div>
              <h3 className="font-display font-bold text-xl text-ink">The marketplace is just getting started</h3>
              <p className="mt-2 text-slate-500">Be the first to list something. Your old textbooks, cycle or gadgets could be exactly what another SRM KTR student needs.</p>
              <Button onClick={onSell} className="mt-6 bg-ink hover:bg-slate-800 text-white h-11 rounded-full px-6" data-testid="empty-list-first-btn">
                <Plus size={18} className="mr-1.5" /> List the first item
              </Button>
            </div>
          )
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
            {listings.map((l) => (
              <ListingCard key={l.id} listing={l} onOpen={setActive} onFavorite={onFavorite} />
            ))}
          </div>
        )}
      </div>

      <ProductDetail listing={active} onClose={() => setActive(null)} onBuy={onBuy} onChat={onChat} onFavorite={onFavorite} user={user} onManage={onManage} />
      <BuyDrawer listing={buyDrawer} open={!!buyDrawer} onOpenChange={(v) => !v && setBuyDrawer(null)} />
      <CreateListingDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
    </div>
  );
}
